import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Switch, Tag, Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, CheckCircleOutline, RadioButtonUnchecked } from '@material-ui/icons';
import { InfoCircleOutlined } from '@ant-design/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import { SecretRefInput } from 'components/Basic/SensitiveInput';
import { useLazyGetIntegrationQuery, useUpdateIntegrationMutation } from 'core/integrations/IntegrationsService';
import {
  SALESFORCE_HTTPS_URL_KEYS,
  SALESFORCE_MASKED_VALUE,
  SALESFORCE_PLAIN_KEYS,
  SALESFORCE_REQUIRED_KEYS,
  SALESFORCE_SECRET_KEYS,
  SALESFORCE_SLUG,
  SalesforceIntegrationResponse,
  SalesforceKey,
  SalesforcePlainKey,
  SalesforceSecretKey,
  isSecretRef,
} from 'core/integrations/salesforceTypes';

type PlainValues = Record<SalesforcePlainKey, string>;
type SecretValues = Record<SalesforceSecretKey, string>;
// Where each secret currently comes from, so the admin knows what "leave blank" keeps.
type SecretStatus = 'configured' | 'env' | 'missing';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyPlain = (): PlainValues =>
  SALESFORCE_PLAIN_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {} as PlainValues);
const emptySecrets = (): SecretValues =>
  SALESFORCE_SECRET_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {} as SecretValues);

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const SalesforceConfig: React.FC = () => {
  const intl = useIntl();

  const [enabled, setEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [values, setValues] = useState<PlainValues>(emptyPlain);
  const [initialValues, setInitialValues] = useState<PlainValues>(emptyPlain);
  const [secrets, setSecrets] = useState<SecretValues>(emptySecrets);
  const [envRefs, setEnvRefs] = useState<Partial<Record<SalesforceKey, string>>>({});
  const [replacing, setReplacing] = useState<Set<SalesforcePlainKey>>(new Set());
  const [secretStatus, setSecretStatus] = useState<Partial<Record<SalesforceSecretKey, SecretStatus>>>({});
  const [computed, setComputed] = useState<SalesforceIntegrationResponse['computed']>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Render from the query's live cache entry: after a save the 'Integrations' tag invalidation
  // refetches it, and awaiting our own GET could race with that refetch and show stale data.
  const [getIntegration, { data: integration, fulfilledTimeStamp }] = useLazyGetIntegrationQuery();
  const [updateIntegration] = useUpdateIntegrationMutation();

  // Only uses state setters, so it is stable and safe as an effect dependency.
  const applyResponse = useCallback((res: SalesforceIntegrationResponse) => {
    const settings = res.settings ?? {};
    const plain = emptyPlain();
    const refs: Partial<Record<SalesforceKey, string>> = {};
    const status: Partial<Record<SalesforceSecretKey, SecretStatus>> = {};

    SALESFORCE_PLAIN_KEYS.forEach(key => {
      const value = settings[key];
      if (isSecretRef(value)) refs[key] = value.$secretRef;
      else plain[key] = value ?? '';
    });
    SALESFORCE_SECRET_KEYS.forEach(key => {
      const value = settings[key];
      if (isSecretRef(value)) {
        refs[key] = value.$secretRef;
        status[key] = 'env';
      } else {
        status[key] = value === SALESFORCE_MASKED_VALUE ? 'configured' : 'missing';
      }
    });

    setEnabled(res.enabled);
    setInitialEnabled(res.enabled);
    setValues(plain);
    setInitialValues(plain);
    setSecrets(emptySecrets());
    setEnvRefs(refs);
    setReplacing(new Set());
    setSecretStatus(status);
    setComputed(res.computed);
  }, []);

  const load = useCallback(() => {
    setIsLoading(true);
    setLoadError(false);
    getIntegration({ slug: SALESFORCE_SLUG })
      .unwrap()
      .catch(() => setLoadError(true))
      .finally(() => setIsLoading(false));
  }, [getIntegration]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-seed the form whenever a newer response lands (initial load and the refetch after saving).
  useEffect(() => {
    if (integration) applyResponse(integration as SalesforceIntegrationResponse);
  }, [integration, fulfilledTimeStamp, applyResponse]);

  const isEnvManaged = (key: SalesforcePlainKey) => !!envRefs[key] && !replacing.has(key);

  const errors: Partial<Record<SalesforcePlainKey, string>> = {};
  SALESFORCE_PLAIN_KEYS.forEach(key => {
    const value = values[key].trim();
    // Clearing is only flagged when the field had a value, so a country that never set up
    // events can still save the Marketing Cloud card.
    if (!value && SALESFORCE_REQUIRED_KEYS.includes(key)) {
      if (initialValues[key]) {
        errors[key] = intl.formatMessage({ id: 'This field is required!' });
      } else if (enabled && !initialEnabled) {
        errors[key] = intl.formatMessage({ id: 'Required to activate the Salesforce integration.' });
      }
      return;
    }
    if (!value || isEnvManaged(key)) return;
    if (SALESFORCE_HTTPS_URL_KEYS.includes(key) && !isHttpsUrl(value)) {
      errors[key] = intl.formatMessage({ id: 'Please enter a valid https:// URL.' });
    }
    if (key === 'mcUsersDataExtensionKey' && !UUID_REGEX.test(value)) {
      errors[key] = intl.formatMessage({ id: 'Please enter a valid UUID.' });
    }
  });

  // Only what the admin actually changed: env-managed fields and untouched secrets are omitted,
  // so the backend keeps them (PUT merges settings).
  const buildChangedSettings = (): Record<string, string> => {
    const changed: Record<string, string> = {};
    SALESFORCE_PLAIN_KEYS.forEach(key => {
      if (isEnvManaged(key)) return;
      const value = values[key].trim();
      // Replacing an env ref with nothing would just drop the ref: treat it as no change.
      if (replacing.has(key)) {
        if (value) changed[key] = value;
      } else if (value !== initialValues[key]) {
        changed[key] = value;
      }
    });
    SALESFORCE_SECRET_KEYS.forEach(key => {
      const value = secrets[key].trim();
      if (value) changed[key] = value;
    });
    return changed;
  };

  const onCancel = () => {
    setEnabled(initialEnabled);
    setValues(initialValues);
    setSecrets(emptySecrets());
    setReplacing(new Set());
  };

  const doSave = async (changedSettings: Record<string, string>) => {
    setIsLoading(true);
    try {
      await updateIntegration({
        slug: SALESFORCE_SLUG,
        body: {
          ...(enabled !== initialEnabled ? { enabled } : {}),
          ...(Object.keys(changedSettings).length ? { settings: changedSettings } : {}),
        },
      }).unwrap();
      toast.success({ title: intl.formatMessage({ id: 'Salesforce configuration updated successfully!' }) });
      // The mutation invalidates 'Integrations', so the subscribed query refetches and the effect
      // above re-seeds the form with the saved state.
    } catch (error) {
      // Backend validation errors come as { data: { message: string[] } }.
      const messages = (error as { data?: { message?: unknown } })?.data?.message;
      toast.error({
        title: Array.isArray(messages)
          ? messages.join('. ')
          : intl.formatMessage({ id: 'Error updating Salesforce configuration' }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    if (Object.keys(errors).length) {
      toast.error({ title: intl.formatMessage({ id: 'Please fix the highlighted fields.' }) });
      return;
    }
    const changedSettings = buildChangedSettings();
    if (enabled === initialEnabled && !Object.keys(changedSettings).length) {
      toast.info({ title: intl.formatMessage({ id: 'There are no changes to save.' }) });
      return;
    }
    const rotatesSharedCredential = 'mcClientId' in changedSettings || 'mcClientSecret' in changedSettings;
    Modal.confirm({
      title: intl.formatMessage({ id: 'Save changes' }),
      content: intl.formatMessage({
        id: rotatesSharedCredential ? 'Salesforce shared credential warning' : 'Salesforce save warning',
      }),
      okText: intl.formatMessage({ id: 'Save' }),
      cancelText: intl.formatMessage({ id: 'Cancel' }),
      className: 'integration_confirm_modal',
      onOk: () => doSave(changedSettings),
    });
  };

  const label = (id: string) => (
    <span className="title" style={{ marginBottom: 0 }}>
      <FormattedMessage id={id} />
    </span>
  );

  const plainField = (key: SalesforcePlainKey, labelId: string, placeholder: string) => (
    <Form.Item
      label={label(labelId)}
      validateStatus={errors[key] ? 'error' : undefined}
      help={errors[key]}
      extra={
        envRefs[key] &&
        (isEnvManaged(key) ? (
          <FormattedMessage id="Value is managed as a server environment variable." />
        ) : (
          <Button
            type="link"
            size="small"
            className="integrations_link_btn"
            onClick={() => setReplacing(prev => new Set(Array.from(prev).filter(k => k !== key)))}
          >
            <FormattedMessage id="Keep using the environment variable" />
          </Button>
        ))
      }
      style={{ marginBottom: 14 }}
    >
      {isEnvManaged(key) ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <SecretRefInput envVarName={envRefs[key] as string} />
          <Button
            id={`integration-salesforce-${key}-replace`}
            className="integrations_outline_btn"
            onClick={() => {
              setValues(prev => ({ ...prev, [key]: '' }));
              setReplacing(prev => new Set(Array.from(prev).concat(key)));
            }}
          >
            <FormattedMessage id="Replace" />
          </Button>
        </div>
      ) : (
        <Input
          id={`integration-salesforce-${key}`}
          value={values[key]}
          placeholder={placeholder}
          autoComplete="off"
          onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value }))}
        />
      )}
    </Form.Item>
  );

  const secretHint = (key: SalesforceSecretKey) => {
    const status = secretStatus[key];
    if (status === 'env') {
      return intl.formatMessage(
        { id: 'Currently taken from the environment variable {name}. Type a value to replace it.' },
        { name: envRefs[key] },
      );
    }
    if (status === 'configured') return intl.formatMessage({ id: 'Leave blank to keep current value' });
    return intl.formatMessage({ id: 'Not configured' });
  };

  const secretField = (key: SalesforceSecretKey, labelId: string) => (
    <Form.Item label={label(labelId)} extra={secretHint(key)} style={{ marginBottom: 14 }}>
      <Input.Password
        id={`integration-salesforce-${key}`}
        value={secrets[key]}
        autoComplete="new-password"
        placeholder={
          secretStatus[key] === 'configured' ? SALESFORCE_MASKED_VALUE : intl.formatMessage({ id: 'Enter a new value' })
        }
        onChange={e => setSecrets(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </Form.Item>
  );

  const configuredTag = (configured?: boolean) => (
    <Tag color={configured ? 'success' : 'default'} style={{ margin: 0 }}>
      <FormattedMessage id={configured ? 'Configured' : 'Not configured'} />
    </Tag>
  );

  if (loadError) {
    return (
      <div className="integrations_layout">
        <Card style={{ borderRadius: 8 }}>
          <p className="title">
            <FormattedMessage id="Could not load the Salesforce configuration." />
          </p>
          <Button id="integration-salesforce-retry" className="integrations_outline_btn" onClick={load}>
            <FormattedMessage id="Retry" />
          </Button>
        </Card>
      </div>
    );
  }

  const statusCardStyle = {
    borderColor: enabled ? '#b7eb8f' : '#ffccc7',
    background: enabled ? '#f6ffed' : '#fff2f0',
    borderRadius: 8,
  };

  return (
    <div className="integrations_layout">
      <Loading visible={isLoading} />

      <div className="integrations_status_card">
        <Card style={statusCardStyle}>
          <div className="status_row">
            <div className="status_info">
              <p className="title" style={{ marginBottom: 4 }}>
                <FormattedMessage id="Salesforce Events" />{' '}
                <Tooltip
                  title={intl.formatMessage({
                    id: 'Only controls sending events. Marketing Cloud sync and the preferences API keep working.',
                  })}
                >
                  <InfoCircleOutlined
                    style={{ color: '#888' }}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </Tooltip>
              </p>
              <div className="status_badge">
                {enabled ? (
                  <CheckCircleOutline style={{ fontSize: 15, color: '#52c41a' }} />
                ) : (
                  <RadioButtonUnchecked style={{ fontSize: 15, color: '#ff4d4f' }} />
                )}
                <Tag color={enabled ? 'success' : 'error'} style={{ margin: 0 }}>
                  <FormattedMessage id={enabled ? 'Active' : 'Inactive'} />
                </Tag>
              </div>
            </div>
            <Switch id="integration-salesforce-enabled" checked={enabled} onChange={setEnabled} />
          </div>
        </Card>
      </div>

      <div className="integrations_grid">
        <div className="integrations_left_col">
          <Card
            title={<FormattedMessage id="Events" />}
            extra={configuredTag(computed?.eventsCredentialsConfigured)}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <Form layout="vertical" colon={false}>
              {plainField('eventsBaseUrl', 'Salesforce Base URL', 'https://your-instance.my.salesforce.com/api')}
              {secretField('eventsApiKey', 'API Key')}
              {secretField('eventsApiSecret', 'API Secret')}
            </Form>
          </Card>
        </div>

        <div className="integrations_right_col">
          <Card
            title={<FormattedMessage id="Marketing Cloud / Preferences API" />}
            extra={configuredTag(computed?.mcCredentialsConfigured)}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 14 }}
              message={intl.formatMessage({
                id: 'Salesforce also uses this Client ID and Client Secret to call our preferences API. Changing them here updates both directions.',
              })}
            />
            <Form layout="vertical" colon={false}>
              {plainField('mcAuthUrl', 'Auth URL', 'https://xxxx.auth.marketingcloudapis.com/v2/token')}
              {plainField('mcRestUrl', 'REST URL', 'https://xxxx.rest.marketingcloudapis.com')}
              {plainField(
                'mcUsersDataExtensionKey',
                'Users Data Extension Key',
                'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
              )}
              {plainField('mcClientId', 'Client ID', '')}
              {secretField('mcClientSecret', 'Client Secret')}
            </Form>
          </Card>
        </div>
      </div>

      <div className="integrations_actions">
        <Button id="integration-salesforce-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-salesforce-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>
    </div>
  );
};

export default SalesforceConfig;
